import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { WorkerActivityMonitor } from '../../hooks/useWorkerActivity';
import { isBadSeverity } from '../../api/workerActivity';
import { formatAge, formatCount } from '../../api/queueMonitor';
// Its own import rather than the diagnosis panel's: this panel is drawn straight on the
// page now, and it should not lose its styling the day that panel is taken out.
import './Health.scss';

export interface IWorkerActivityPanelProps {
    monitor: WorkerActivityMonitor;
}

/** The windows on offer. Long enough to cover a night, short enough to stay quick. */
const WINDOWS = [1, 6, 24, 72];

/**
 * What one function app has been doing, from its own telemetry.
 *
 * This is the panel that replaces opening Application Insights in another tab and writing a
 * Kusto query. Three things are on it, in the order they answer questions: whether the host
 * started, which functions ran and how they ended, and which exceptions accounted for the
 * failures. The order matters - a host that never started makes everything below it a
 * consequence, so it is on top and the summary says so before anyone reads further.
 */
export const WorkerActivityPanel = ({ monitor }: IWorkerActivityPanelProps) => {
    const { appKey, activity, loading, error, hours } = monitor;

    if (!appKey) {
        return null;
    }

    return (
        <div className="activity" aria-live="polite">
            <div className="activity__head">
                <strong className="activity__title">
                    {activity?.displayName ?? appKey}
                </strong>

                {activity?.roleName && (
                    <code className="activity__role">{activity.roleName}</code>
                )}

                <span className="activity__windows">
                    {WINDOWS.map((window) => (
                        <button
                            type="button"
                            key={window}
                            className={`activity__window${
                                hours === window
                                    ? ' activity__window--active'
                                    : ''
                            }`}
                            onClick={() => monitor.setHours(window)}
                        >
                            {window === 1 ? '1 uur' : `${window} uur`}
                        </button>
                    ))}
                </span>

                <button
                    type="button"
                    className="activity__close"
                    onClick={monitor.close}
                    title="Sluiten"
                >
                    <FontAwesomeIcon icon="xmark" />
                </button>
            </div>

            {loading && (
                <p className="activity__placeholder">
                    <FontAwesomeIcon icon="spinner" spin /> telemetrie ophalen...
                </p>
            )}

            {error && (
                <p className="activity__error" role="alert">
                    {error}
                </p>
            )}

            {/*
                A missing key is not a failure, it is a setup step. Saying which secret to
                create beats an empty panel that leaves everyone guessing whether the app is
                quiet or the plumbing is.
            */}
            {activity && !activity.configured && (
                <p className="activity__unconfigured">
                    <FontAwesomeIcon icon="circle-info" className="me-2" />
                    {activity.error}
                </p>
            )}

            {activity?.configured && (
                <>
                    {activity.summary && (
                        <p className="activity__summary">{activity.summary}</p>
                    )}

                    {activity.error && (
                        <p className="activity__error">{activity.error}</p>
                    )}

                    {activity.invocations.length > 0 && (
                        <section className="activity__block">
                            <h5 className="activity__block-title">
                                Verwerkingen
                            </h5>

                            <table className="activity__table">
                                <thead>
                                    <tr>
                                        <th>Functie</th>
                                        <th>Aantal</th>
                                        <th>Mislukt</th>
                                        <th>Laatst</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activity.invocations.map((invocation) => (
                                        <tr key={invocation.functionName}>
                                            <td>
                                                <code>
                                                    {invocation.functionName}
                                                </code>
                                            </td>
                                            <td>
                                                {formatCount(
                                                    invocation.total ?? 0
                                                )}
                                            </td>
                                            <td
                                                className={
                                                    (invocation.failed ?? 0) > 0
                                                        ? 'activity__bad'
                                                        : undefined
                                                }
                                            >
                                                {formatCount(
                                                    invocation.failed ?? 0
                                                )}
                                            </td>
                                            <td>
                                                {formatAge(
                                                    invocation.lastAt
                                                ) ?? '-'}
                                                {invocation.lastAt
                                                    ? ' geleden'
                                                    : ''}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    )}

                    {activity.errors.length > 0 && (
                        <section className="activity__block">
                            <h5 className="activity__block-title">
                                Waarom het misging
                            </h5>

                            <ul className="activity__errors">
                                {activity.errors.map((entry, index) => (
                                    <li
                                        className="activity__error-row"
                                        key={`${entry.type}-${index}`}
                                    >
                                        <span className="activity__occurrences">
                                            {formatCount(
                                                entry.occurrences ?? 0
                                            )}
                                            x
                                        </span>

                                        <span className="activity__error-body">
                                            <span className="activity__error-message">
                                                {entry.message}
                                            </span>

                                            <span className="activity__error-meta">
                                                {entry.type}
                                                {entry.functionName
                                                    ? ` · ${entry.functionName}`
                                                    : ''}
                                                {entry.lastAt
                                                    ? ` · laatst ${formatAge(
                                                          entry.lastAt
                                                      )} geleden`
                                                    : ''}
                                            </span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {activity.hostEvents.length > 0 && (
                        <section className="activity__block">
                            <h5 className="activity__block-title">
                                Wat de host zelf logde
                            </h5>

                            <ul className="activity__events">
                                {activity.hostEvents.map((event, index) => (
                                    <li
                                        className={`activity__event${
                                            isBadSeverity(event.severity)
                                                ? ' activity__event--bad'
                                                : ''
                                        }`}
                                        key={`${event.at}-${index}`}
                                    >
                                        <span className="activity__event-when">
                                            {formatAge(event.at) ?? '-'}
                                        </span>

                                        <span className="activity__event-message">
                                            {event.message}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {!loading &&
                        activity.invocations.length === 0 &&
                        activity.errors.length === 0 &&
                        activity.hostEvents.length === 0 && (
                            <p className="activity__placeholder">
                                Geen enkele regel van deze app over de afgelopen{' '}
                                {hours} uur.
                            </p>
                        )}
                </>
            )}
        </div>
    );
};
